import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { remindersAPI, signaturesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import SmartReminders from '../../components/agency/SmartReminders';
import ShareTriggerBanner from '../../components/agent/ShareTriggerBanner';
import AiActionsWidget from '../../components/dashboard/AiActionsWidget';
import KPIBouquetsStrip from '../../components/cockpit/KPIBouquetsStrip';
import { toast } from 'sonner';
import {
    FileText,
    Plus,
    Upload,
    Clock,
    ArrowRight,
    PenTool,
    CheckCircle,
    Sparkles,
    Globe,
    ExternalLink,
    Send,
    HelpCircle,
    FileDown,
    Loader2,
    FolderClosed,
    Euro
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
    draft: { label: 'Brouillon', color: 'bg-slate-500/20 text-slate-400', icon: Clock },
    generated: { label: 'PDF généré', color: 'bg-blue-500/20 text-blue-400', icon: FileText },
    sent: { label: 'Envoyé', color: 'bg-amber-500/20 text-amber-400', icon: Send },
    acknowledged: { label: 'Validé', color: 'bg-green-500/20 text-green-400', icon: CheckCircle }
};

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
};

// Helper to format date
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    } catch {
        return '';
    }
};

// Helper to generate slug from name
const generateSlug = (name) => {
    let slug = name.toLowerCase().trim();
    slug = slug.replace(/[àáâãäå]/g, 'a');
    slug = slug.replace(/[èéêë]/g, 'e');
    slug = slug.replace(/[ìíîï]/g, 'i');
    slug = slug.replace(/[òóôõö]/g, 'o');
    slug = slug.replace(/[ùúûü]/g, 'u');
    slug = slug.replace(/[ç]/g, 'c');
    slug = slug.replace(/[^a-z0-9]+/g, '-');
    slug = slug.replace(/^-|-$/g, '');
    return slug;
};

function AgentDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [mandates, setMandates] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [pendingSignatures, setPendingSignatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatingReport, setGeneratingReport] = useState(false);

    // Generate my performance report PDF
    const handleGenerateMyReport = async () => {
        setGeneratingReport(true);
        try {
            const response = await api.get('/reports/agent/me?period=month', { responseType: 'blob' });
            
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `mon_rapport_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success('Rapport PDF généré !');
        } catch (err) {
            console.error('Report generation error:', err);
            toast.error('Erreur lors de la génération du rapport');
        } finally {
            setGeneratingReport(false);
        }
    };

    // Check onboarding status
    const checkOnboarding = useCallback(async () => {}, []);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            const [mandatesRes, remindersRes, signaturesRes] = await Promise.all([
                api.get('/mandates-v2/'),
                remindersAPI.getAll(false),
                signaturesAPI.getPending()
            ]);
            
            const allMandates = mandatesRes.data.mandates || [];
            
            // Calculate stats from mandates
            const calculatedStats = {
                total_mandates: allMandates.length,
                active_mandates: allMandates.filter(m => ['draft', 'generated', 'sent', 'active', 'actif', 'acknowledged'].includes(m.status)).length,
                pending_signatures: allMandates.filter(m => m.status === 'sent').length,
                avg_rating: 0
            };
            
            setStats(calculatedStats);
            setMandates(allMandates);
            setReminders(remindersRes.data.slice(0, 5));
            setPendingSignatures(signaturesRes.data);
        } catch (err) {
            console.error('Dashboard error:', err);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const completeReminder = async (reminderId) => {
        try {
            await remindersAPI.complete(reminderId);
            setReminders(reminders.filter(r => r.id !== reminderId));
            toast.success('Rappel marqué comme terminé');
        } catch (err) {
            toast.error('Erreur');
        }
    };

    // Group mandates by status for pipeline view (using new v2 statuses)
    const pipeline = {
        brouillon: mandates.filter(m => m.status === 'draft'),
        en_signature: mandates.filter(m => ['generated', 'sent'].includes(m.status)),
        actif: [],
        termine: mandates.filter(m => m.status === 'acknowledged')
    };

    const kpiCards = [
        {
            title: 'Total mandats',
            value: stats?.total_mandates || 0,
            icon: FileText,
            gradient: 'from-[#681793] to-[#9b4dca]'
        },
        {
            title: 'Actifs',
            value: stats?.active_mandates || 0,
            icon: CheckCircle,
            gradient: 'from-green-500 to-emerald-400'
        },
        {
            title: 'Note moyenne',
            value: stats?.average_rating?.toFixed(1) || '-',
            icon: Sparkles,
            gradient: 'from-[#FF914D] to-[#ffb380]'
        },
        {
            title: 'À signer',
            value: pendingSignatures.length,
            icon: PenTool,
            gradient: 'from-blue-500 to-cyan-400'
        }
    ];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-card rounded-2xl p-6 border border-border">
                            <Skeleton className="h-16 w-full bg-muted" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <>
            <motion.div 
                className="space-y-4 sm:space-y-6" 
                data-testid="agent-dashboard"
                initial="hidden"
                animate="visible"
                variants={stagger}
            >
                {/* Header — Clean, focused */}
                <motion.div variants={fadeIn} className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl sm:text-3xl font-black text-foreground truncate">
                                Bonjour, {user?.first_name}
                        </h1>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">
                            {stats?.total_mandates > 0
                                ? <>{stats.total_mandates} mandat{stats.total_mandates > 1 ? 's' : ''} en cours
                                {stats?.signed_mandates > 0 && <> — <span className="text-green-400">{stats.signed_mandates} signé{stats.signed_mandates > 1 ? 's' : ''}</span></>}
                                {reminders.length > 0 && <> — <span className="text-[#FF914D]">{reminders.length} rappel{reminders.length > 1 ? 's' : ''}</span></>}
                                </>
                                : 'Créez votre premier mandat pour démarrer'}
                        </p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                        {/* PDF Report button - hidden on mobile */}
                        <Button 
                            variant="outline"
                            onClick={handleGenerateMyReport}
                            disabled={generatingReport}
                            className="hidden sm:flex border-border text-muted-foreground hover:text-white hover:bg-card hover:border-blue-500/50"
                            title="Télécharger mon rapport"
                        >
                            {generatingReport ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <FileDown className="w-4 h-4 mr-2" />
                            )}
                            Mon rapport
                        </Button>
                        <Link to="/agent/mandates/upload">
                            <Button 
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white shadow-lg shadow-[#681793]/30 text-sm px-3 sm:px-4"
                                data-testid="new-mandate-button"
                            >
                                <Upload className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Uploader un mandat</span>
                            </Button>
                        </Link>
                    </div>
                </motion.div>

                {/* Vue synthétique — 5 bouquets KPI contextuels (P3) */}
                <motion.div variants={fadeIn} data-testid="agent-bouquets-strip">
                    <KPIBouquetsStrip scope="mine" variant="agent" />
                </motion.div>

                {/* Déclencheur viral — invite au partage après avis 5⭐ ou mandat signé */}
                <motion.div variants={fadeIn}>
                    <ShareTriggerBanner />
                </motion.div>

                {/* AI Actions — The "wow moment" */}
                <motion.div variants={fadeIn}>
                    <AiActionsWidget basePath="/agent" />
                </motion.div>

                {/* Smart Reminders */}
                <motion.div variants={fadeIn}>
                    <SmartReminders basePath="/agent" />
                </motion.div>

                {/* Empty State or KPI Cards + Pipeline */}
                {stats?.total_mandates === 0 && mandates.length === 0 ? (
                    <motion.div variants={fadeIn}>
                        <div className="relative overflow-hidden rounded-2xl border border-[#681793]/30 bg-gradient-to-br from-[#681793]/10 via-card to-[#FF914D]/10 p-6 sm:p-10" data-testid="agent-empty-state">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-[#681793]/10 rounded-full blur-[100px]" />
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#FF914D]/10 rounded-full blur-[80px]" />
                            <div className="relative text-center max-w-lg mx-auto">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center mx-auto mb-5 shadow-2xl shadow-[#681793]/30">
                                    <FileText className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                </div>
                                <h2 className="text-xl sm:text-2xl font-black text-foreground mb-2">
                                    Prêt à démarrer ?
                                </h2>
                                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                                    Uploadez le PDF ou photographiez votre mandat papier signé — l'IA extrait les infos en quelques secondes et débloque votre assistant IA.
                                </p>
                                <Link to="/agent/mandates/upload">
                                    <Button 
                                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white font-bold px-8 h-12 shadow-xl shadow-[#681793]/30 text-base"
                                        data-testid="empty-state-create-mandate-agent"
                                    >
                                        <Upload className="w-5 h-5 mr-2" />
                                        Uploader mon premier mandat
                                    </Button>
                                </Link>
                                <div className="mt-8 grid grid-cols-3 gap-4">
                                    {[
                                        { icon: FileText, label: 'Suivi temps réel' },
                                        { icon: Sparkles, label: 'IA Auto-pilote' },
                                        { icon: PenTool, label: 'Signature en ligne' }
                                    ].map((f, i) => (
                                        <div key={i} className="flex flex-col items-center gap-1.5">
                                            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                                                <f.icon className="w-4 h-4 text-[#FF914D]" />
                                            </div>
                                            <p className="text-[10px] sm:text-xs text-muted-foreground">{f.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                <>
                {/* KPI Cards */}
                <motion.div 
                    className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4"
                    variants={stagger}
                >
                    {kpiCards.map((kpi) => (
                        <motion.div 
                            key={kpi.title}
                            variants={fadeIn}
                            whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                        >
                            <div className="relative group bg-card backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-border hover:border-white/20 transition-all duration-300 overflow-hidden">
                                <div className={`absolute inset-0 bg-gradient-to-br ${kpi.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                                <div className="relative flex items-center gap-2.5 sm:gap-3">
                                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${kpi.gradient} flex items-center justify-center shadow-lg shrink-0`}>
                                        <kpi.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{kpi.title}</p>
                                        <p className="text-xl sm:text-2xl font-black text-foreground">{kpi.value}</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Pipeline des mandats — Core feature */}
                <motion.div variants={fadeIn}>
                    <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#681793]" />
                                Pipeline des mandats
                            </h2>
                            <Link to="/agent/mandates">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-muted-foreground hover:text-white hover:bg-card"
                                    data-testid="view-pipeline-button"
                                >
                                    Voir tout
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                        <div className="p-5">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {Object.entries(pipeline).map(([status, items]) => (
                                    <div key={status} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-muted-foreground uppercase">
                                                {status === 'termine' ? 'Validé' : 
                                                 status === 'en_signature' ? 'En cours' :
                                                 status === 'brouillon' ? 'Brouillon' : 'Actif'}
                                            </span>
                                            <Badge className="bg-muted text-muted-foreground text-xs border-0">
                                                {items.length}
                                            </Badge>
                                        </div>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {items.slice(0, 3).map((mandate) => {
                                                const salePrice = mandate.pricing?.sale_price || 0;
                                                return (
                                                    <Link key={mandate.id} to={`/agent/mandates/v2/${mandate.id}`}>
                                                        <div className="p-3 rounded-xl bg-card hover:bg-muted border border-border hover:border-border transition-all text-xs">
                                                            <p className="font-medium text-foreground truncate">{mandate.mandate_number}</p>
                                                            <p className="text-muted-foreground">
                                                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(salePrice)}
                                                            </p>
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                            {items.length > 3 && (
                                                <p className="text-xs text-white/30 text-center">+{items.length - 3} autres</p>
                                            )}
                                            {items.length === 0 && (
                                                <div className="p-3 rounded-xl bg-card border border-dashed border-border text-center">
                                                    <p className="text-xs text-white/30">Aucun</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
                </>
                )}

                {/* Accès rapides — nouveaux modules P2 */}
                <motion.div variants={fadeIn} data-testid="agent-quick-access">
                    <div className="bg-gradient-to-br from-[#681793]/15 to-[#FF914D]/15 rounded-2xl p-5 border border-[#681793]/30">
                        <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#FF914D]" />
                            Mes outils
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-2">
                            <Link to="/notaire/dossiers" data-testid="agent-nav-notary"
                                  className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-[#681793]/50 transition">
                                <FolderClosed className="w-5 h-5 text-[#681793]" />
                                <div>
                                    <p className="text-sm font-semibold text-foreground">Dossiers notaire</p>
                                    <p className="text-xs text-muted-foreground">Checklist des pièces + partage notaire</p>
                                </div>
                            </Link>
                            <Link to="/agent/commissions" data-testid="agent-nav-commissions"
                                  className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-[#681793]/50 transition">
                                <Euro className="w-5 h-5 text-[#FF914D]" />
                                <div>
                                    <p className="text-sm font-semibold text-foreground">Mes commissions</p>
                                    <p className="text-xs text-muted-foreground">Rémunérations HT/TTC + bordereau PDF</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </motion.div>

                {/* Pending signatures alert */}
                {pendingSignatures.length > 0 && (
                    <motion.div variants={fadeIn}>
                        <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl p-5 border border-blue-500/20">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                                    <PenTool className="w-6 h-6 text-blue-400" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-foreground">
                                        {pendingSignatures.length} document(s) à signer
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Des signatures sont en attente de votre part
                                    </p>
                                </div>
                                <Button 
                                    className="bg-blue-500 hover:bg-blue-600 text-white"
                                    data-testid="sign-documents-button"
                                >
                                    Signer
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </motion.div>
        </>
    );
}

export default AgentDashboard;
